/**
 * SFrame105 – Account Creation (Step 2 of 3)
 * Figma Frame 5 (node 1018:13539)
 *
 * "It's a privilege to help you."
 * Collects: email, first/last name, role selection, responsibility acknowledgment.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../utils/api';
import radioChecked from '../../assets/images/radio-checked.svg';
import radioUnchecked from '../../assets/images/radio-unchecked.svg';

interface LocationState {
  situationType?: string;
}

export default function SFrame105() {
  const navigate = useNavigate();
  const location = useLocation();
  const { situationType } = (location.state as LocationState) || {};

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password] = useState('');
  const [role, setRole] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Check if already logged in
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('account-token');
    if (token) setIsLoggedIn(true);
  }, []);

  const isFormValid = firstName.trim() && lastName.trim() && email.trim() && role && acknowledged;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsSubmitting(true);
    setError('');

    try {
      if (isLoggedIn) {
        navigate('/sframe-107', { state: { situationType, role } });
        return;
      }

      const response = await apiService.createAccount({
        email: email.trim().toLowerCase(),
        name: `${firstName.trim()} ${lastName.trim()}`,
        password: password || 'temp-password-' + Date.now()
      });

      if (response.success) {
        navigate('/verify-email-pending', {
          state: {
            email: email.trim().toLowerCase(),
            settlementOnboarding: true,
            situationType,
            role,
          },
        });
      } else {
        setError(response.message || 'Failed to create account. Please try again.');
      }
    } catch (err) {
      console.error('[SFrame105] Account creation error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top header bar */}
      <div className="bg-dark-gradient py-3 px-6">
        <p className="text-center text-white-alt font-semibold text-base tracking-wide">
          Estate Settlement
        </p>
      </div>

      {/* Background: cream top, light blue bottom */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 bg-gradient-to-b from-white to-blue-100">
        <div className="w-full max-w-[520px] bg-white-alt rounded-xl shadow-card p-8 sm:p-10">
          <h1 className="font-display text-2xl text-blue-900 mb-8">
            It's a privilege to help you.
          </h1>

          {isLoggedIn ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-black text-sm mb-6">You're already signed in. We'll use your existing account.</p>
              <button
                onClick={() => navigate('/sframe-107', { state: { situationType, role: 'executor' } })}
                className="px-6 py-2.5 rounded-md font-medium text-sm bg-blue-300 text-white-alt hover:bg-blue-600 transition-all"
              >
                Continue
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-black mb-2">
                  Your Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-grey-300 px-4 py-3 text-sm text-black placeholder:text-grey-600 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none transition"
                  placeholder="you@email.com"
                  required
                />
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  Your Name
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full rounded-md border border-grey-300 px-4 py-3 text-sm text-black placeholder:text-grey-600 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none transition"
                    placeholder="First"
                    required
                  />
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full rounded-md border border-grey-300 px-4 py-3 text-sm text-black placeholder:text-grey-600 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none transition"
                    placeholder="Last"
                    required
                  />
                </div>
              </div>

              {/* Role Selection */}
              <div>
                <label htmlFor="role" className="block text-sm font-semibold text-black mb-2">
                  Role Selection
                </label>
                <div className="relative">
                  <select
                    id="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-full rounded-md border border-grey-300 px-4 py-3 text-sm text-black bg-white-alt focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none transition appearance-none cursor-pointer"
                    required
                  >
                    <option value="" disabled></option>
                    <option value="executor">Executor</option>
                    <option value="co-executor">Co-Executor</option>
                    <option value="family">Family Member</option>
                    <option value="attorney">Attorney</option>
                    <option value="other">Other</option>
                  </select>
                  <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey-600 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Acknowledgment */}
              <label className="flex items-start gap-3 cursor-pointer" onClick={() => setAcknowledged(!acknowledged)}>
                <img
                  src={acknowledged ? radioChecked : radioUnchecked}
                  alt=""
                  className="w-[18px] h-[18px] shrink-0 mt-0.5"
                />
                <span className="text-sm text-black leading-relaxed">
                  I understand that I am responsible for the accuracy of the information provided.
                </span>
              </label>

              {/* Error */}
              {error && (
                <div className="bg-orange-100 border border-orange-300 rounded-md p-3 text-orange-900 text-sm">
                  {error}
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={() => navigate('/sframe-101', { state: { situationType } })}
                  className="text-black hover:text-blue-900 text-sm font-medium transition-colors flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-md font-medium text-sm transition-all ${
                    isFormValid && !isSubmitting
                      ? 'bg-blue-300 text-white-alt hover:bg-blue-600'
                      : 'bg-grey-300 text-grey-600 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? 'Creating...' : 'Next'}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
